// src/components/ProductCard.jsx
import React, { useState } from 'react';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product }) => {
  const [selectedOption, setSelectedOption] = useState(product.priceOptions?.[0] || null);
  const [isAdding, setIsAdding] = useState(false);
  const { addToCart } = useCart();

  const handleAddToCart = async () => {
    setIsAdding(true);
    
    // Simulate loading
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (product.priceOptions) {
      addToCart(product, selectedOption);
    } else {
      addToCart(product);
    }
    
    setIsAdding(false);
  };

  const getStrainColor = (strain) => {
    switch(strain) {
      case 'Sativa': return { background: '#fef3c7', color: '#92400e' };
      case 'Indica': return { background: '#e9d5ff', color: '#6b21a8' };
      case 'Hybrid': return { background: '#d1fae5', color: '#065f46' };
      default: return { background: '#e5e7eb', color: '#374151' };
    }
  };

  const strainStyle = getStrainColor(product.strain);

  return (
    <div className="card" style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Image */}
      <div style={{
        position: 'relative',
        height: '200px',
        minHeight: '200px',
        overflow: 'hidden',
        flexShrink: 0
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '3.5rem'
        }}>
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '🌿';
              }}
            />
          ) : (
            '🌿'
          )}
        </div>
        
        {/* Badges */}
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {product.isNew && (
            <span style={{
              padding: '4px 10px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              borderRadius: '20px',
              fontSize: '0.7rem',
              fontWeight: '600',
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              NEW
            </span>
          )}
          <span style={{
            padding: '4px 10px',
            background: strainStyle.background,
            color: strainStyle.color,
            borderRadius: '20px',
            fontSize: '0.7rem',
            fontWeight: '600',
            lineHeight: 1,
            letterSpacing: '0.05em'
          }}>
            {product.strain}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ 
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0
      }}>
        {/* Category */}
        <div style={{
          fontSize: '0.75rem',
          color: 'var(--text-light)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '8px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {product.category.replace('-', ' ')}
        </div>

        {/* Name */}
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '700',
          marginBottom: '8px',
          lineHeight: 1.3,
          color: 'var(--text)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.6em'
        }}>
          {product.name}
        </h3>

        {/* Description */}
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--text-light)',
          marginBottom: '20px',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flexGrow: 1
        }}>
          {product.description}
        </p>

        {/* Options */}
        {product.priceOptions ? (
          <div style={{ 
            marginBottom: '20px',
            flexShrink: 0 
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
              gap: '8px'
            }}>
              {product.priceOptions.map((opt) => (
                <button
                  key={opt.option}
                  onClick={() => setSelectedOption(opt)}
                  style={{
                    padding: '8px 4px',
                    fontSize: '0.75rem',
                    borderRadius: '6px',
                    border: selectedOption?.option === opt.option ? 
                      '2px solid var(--primary)' : '1px solid var(--border)',
                    background: selectedOption?.option === opt.option ? 
                      'var(--primary)' : 'var(--background)',
                    color: selectedOption?.option === opt.option ? 
                      'white' : 'var(--text)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.2
                  }}
                >
                  <span>{opt.option}</span>
                  <span style={{ 
                    fontSize: '0.7rem',
                    opacity: 0.8,
                    marginTop: '2px'
                  }}>
                    ${opt.price}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            fontSize: '0.875rem',
            color: 'var(--text-light)',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            flexShrink: 0
          }}>
            {product.thcContent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontWeight: '600' }}>THC:</span>
                <span>{product.thcContent}mg</span>
              </div>
            )}
            {product.weight && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontWeight: '600' }}>Weight:</span>
                <span>{product.weight}</span>
              </div>
            )}
          </div>
        )}

        {/* Add to Cart Button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: 'auto',
          flexShrink: 0
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'var(--primary)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              ${product.priceOptions ? selectedOption?.price : product.price}
            </div>
            {product.priceOptions && (
              <div style={{
                fontSize: '0.75rem',
                color: 'var(--text-light)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {selectedOption?.option}
              </div>
            )}
          </div>
          
          <button
            onClick={handleAddToCart}
            disabled={isAdding}
            style={{
              padding: '10px 16px',
              fontSize: '0.875rem',
              fontWeight: '600',
              borderRadius: '8px',
              border: 'none',
              background: isAdding ? 'var(--primary-dark)' : 'var(--primary)',
              color: 'white',
              cursor: isAdding ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexShrink: 0,
              opacity: isAdding ? 0.7 : 1
            }}
          >
            {isAdding ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Adding...
              </>
            ) : (
              'Add to Cart'
            )}
          </button>
        </div>
      </div>

      {/* Add media query for mobile optimization */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 480px) {
          .card {
            padding: 12px;
          }
          
          /* Adjust price options grid for mobile */
          .card > div > div > div:nth-child(4) > div {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 6px !important;
          }
          
          .card > div > div > div:nth-child(4) > div > button {
            font-size: 0.7rem !important;
            padding: 6px 2px !important;
          }
        }
        
        @media (max-width: 375px) {
          .card > div > div > div:nth-child(4) > div {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 4px !important;
          }
          
          .card > div > div > div:nth-child(4) > div > button {
            font-size: 0.65rem !important;
            padding: 4px 1px !important;
          }
          
          .card > div > div > div:nth-child(6) > button {
            min-width: 90px !important;
            padding: 8px 12px !important;
            font-size: 0.8rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;